import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'
import type { Location, PrescriberJoinedLocationProfile } from '../../../types/location'
import { useGetLocationsQuery } from '../locationSlice'
import { useGetPrescriberLocationProfilesQuery } from '../prescriberLocationProfilesSlice'

export function useOrganizationLocations(skip = false) {
  const { data: locations = [], isLoading: isLoadingLocations } = useGetLocationsQuery(
    skip ? skipToken : undefined
  )

  const { data: prescriberProfiles = [], isLoading: isLoadingLocationProfiles } =
    useGetPrescriberLocationProfilesQuery(skip ? skipToken : undefined)

  const joinedLocationIds = useMemo(
    () => new Set(prescriberProfiles.map((p) => p.locationId)),
    [prescriberProfiles]
  )

  const locationById = useMemo(() => new Map(locations.map((loc) => [loc.id, loc])), [locations])

  const joinedPrescriberLocationProfiles = useMemo((): PrescriberJoinedLocationProfile[] => {
    return prescriberProfiles.reduce<PrescriberJoinedLocationProfile[]>((acc, profile) => {
      const location = locationById.get(profile.locationId)
      if (location) acc.push({ ...profile, location })
      return acc
    }, [])
  }, [prescriberProfiles, locationById])

  const joinedLocations = useMemo((): Location[] => {
    const byId = new Map<string, Location>()
    for (const profile of joinedPrescriberLocationProfiles) {
      byId.set(profile.location.id, profile.location)
    }
    return [...byId.values()]
  }, [joinedPrescriberLocationProfiles])

  const availableLocations = useMemo(
    () => locations.filter((loc) => !joinedLocationIds.has(loc.id)),
    [locations, joinedLocationIds]
  )

  return {
    locations,
    joinedPrescriberLocationProfiles,
    joinedLocations,
    availableLocations,
    isLoading: isLoadingLocations || isLoadingLocationProfiles,
  }
}
